/**
 * The canvas wire contract — backend JSON shapes and the converters in both
 * directions. Pure functions only, no network, no store: this module is the
 * single translation layer between `snake_case` backend JSON and the camelCase
 * domain types the canvas works in.
 *
 * Both paths that receive canvas data share it:
 *   - `canvasService.fetchCanvas` — the initial load
 *   - `useCanvasStore.applyWsMessage` — live patches from the realtime hub
 *
 * They used to have separate copies, and the WS copy silently drifted: it never
 * learned `bidirectional` (migration 010), so an edge that arrived live rendered
 * one-directional and only grew its second arrowhead after a page reload. One
 * copy, one behaviour.
 *
 * Kept out of `canvasService` on purpose — that module does I/O and gets mocked
 * wholesale in store tests, which would drag these pure functions into the mock.
 */

import type {
  CanvasEdge,
  CanvasNode,
  CanvasShape,
} from "@/store/useCanvasStore";

// ── Wire types (backend snake_case JSON) ──────────────────────────────────────
// These mirror the Go structs in internal/graph/models.go field-for-field. The
// realtime hub publishes the very same JSON for its patch payloads, so a frame's
// payload is interchangeable with an element of the GET /canvas response.

export interface WireNode {
  id: string;
  forge_id: string;
  type: string;
  label: string;
  icon?: string | null;
  x?: number | null;
  y?: number | null;
  width: number;
  height: number;
  layer?: number;
  reveal_step: number;
  drill_forge_id?: string | null;
  color?: string | null;
  page_count?: number;
}

export interface WireEdge {
  id: string;
  forge_id: string;
  source_id: string;
  target_id: string;
  label_text?: string | null;
  label_offset_x: number;
  label_offset_y: number;
  flow_speed: number;
  flow_label?: string | null;
  relationship_kind?: string | null;
  reveal_step: number;
  bidirectional?: boolean;
}

export interface WireShape {
  id: string;
  forge_id: string;
  kind: string;
  content?: string | null;
  x?: number | null;
  y?: number | null;
  width: number;
  height: number;
  z_index: number;
  reveal_step: number;
  x2?: number | null;
  y2?: number | null;
}

export interface WirePayload {
  nodes: WireNode[];
  edges: WireEdge[];
  shapes: WireShape[];
  /** Forge revision this payload was read at — the base for the next save.
   *  Optional: a server from before optimistic concurrency omits it. */
  revision?: number;
}

// ── Mappers (snake_case → camelCase) ─────────────────────────────────────────

export function mapNode(w: WireNode): CanvasNode {
  return {
    id: w.id,
    forgeId: w.forge_id,
    type: w.type,
    label: w.label,
    icon: w.icon ?? undefined,
    x: w.x ?? 0,
    y: w.y ?? 0,
    width: w.width,
    height: w.height,
    layer: w.layer ?? 0,
    revealStep: w.reveal_step,
    drillForgeId: w.drill_forge_id ?? undefined,
    color: w.color ?? undefined,
    pageCount: w.page_count ?? 0,
  };
}

export function mapEdge(w: WireEdge): CanvasEdge {
  return {
    id: w.id,
    forgeId: w.forge_id,
    sourceId: w.source_id,
    targetId: w.target_id,
    labelText: w.label_text ?? undefined,
    labelOffsetX: w.label_offset_x,
    labelOffsetY: w.label_offset_y,
    flowSpeed: w.flow_speed,
    flowLabel: w.flow_label ?? undefined,
    kind: (w.relationship_kind ?? undefined) as CanvasEdge["kind"] | undefined,
    revealStep: w.reveal_step,
    bidirectional: w.bidirectional ?? false,
  };
}

export function mapShape(w: WireShape): CanvasShape {
  return {
    id: w.id,
    forgeId: w.forge_id,
    kind: w.kind,
    content: w.content ?? undefined,
    x: w.x ?? 0,
    y: w.y ?? 0,
    width: w.width,
    height: w.height,
    zIndex: w.z_index,
    revealStep: w.reveal_step,
    x2: w.x2 ?? undefined,
    y2: w.y2 ?? undefined,
  };
}

// ── Reverse mappers (camelCase → snake_case) ──────────────────────────────────

export function toWireNode(n: CanvasNode): WireNode {
  return {
    id: n.id,
    forge_id: n.forgeId,
    type: n.type,
    label: n.label,
    icon: n.icon ?? null,
    x: n.x,
    y: n.y,
    width: n.width,
    height: n.height,
    layer: n.layer ?? 0,
    reveal_step: n.revealStep,
    drill_forge_id: n.drillForgeId ?? null,
    color: n.color ?? null,
  };
}

export function toWireEdge(e: CanvasEdge): WireEdge {
  return {
    id: e.id,
    forge_id: e.forgeId,
    source_id: e.sourceId,
    target_id: e.targetId,
    label_text: e.labelText ?? null,
    label_offset_x: e.labelOffsetX,
    label_offset_y: e.labelOffsetY,
    flow_speed: e.flowSpeed,
    flow_label: e.flowLabel ?? null,
    relationship_kind: e.kind ?? null,
    reveal_step: e.revealStep,
    bidirectional: e.bidirectional ?? false,
  };
}

export function toWireShape(s: CanvasShape): WireShape {
  return {
    id: s.id,
    forge_id: s.forgeId,
    kind: s.kind,
    content: s.content ?? null,
    x: s.x,
    y: s.y,
    width: s.width,
    height: s.height,
    z_index: s.zIndex,
    reveal_step: s.revealStep,
    x2: s.x2 ?? null,
    y2: s.y2 ?? null,
  };
}
